import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarBlank,
  Storefront,
  Truck,
  Images,
  ListChecks,
  SignOut,
  Cube,
  List,
  X,
  SquaresFour,
  Kanban,
  FunnelSimple,
  Briefcase,
  Stack,
  Rows,
  PuzzlePiece,
  ChartBar,
  Users,
  UsersThree,
  HardHat,
  Gear,
  UserCircle,
  Package,
  Drop,
  Calculator,
  Receipt,
  FolderSimple,
  Wrench,
  Sun,
  Moon,
  Sparkle,
  PencilRuler,
  SidebarSimple,
  DeviceMobile,
  Tray,
  EnvelopeSimple,
  ChatCircle,
  ArrowUUpLeft,
  Lightning,
  Buildings,
  AddressBook,
  CaretDown,
  NavigationArrow,
  Plus,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { api } from "@/lib/api";
import { applyTheme, getTheme, applyShopTheme } from "@/lib/theme";
import { TasksPopover } from "@/components/TasksPopover";
import { AlertsBell } from "@/components/AlertsBell";
import { blockerCount, useAttention } from "@/lib/blockers";
import { GlobalSearch, GlobalSearchMobile } from "@/components/GlobalSearch";

const INVENTORY_CHILDREN = [
  { to: "/inventory/slabs", label: "Inventory", icon: Stack },
  { to: "/inventory/cabinetry", label: "Cabinetry", icon: Package },
  { to: "/inventory/remnants", label: "Remnants", icon: PuzzlePiece },
  { to: "/inventory/sinks", label: "Sinks", icon: Drop },
  { to: "/inventory/material-on-hold", label: "Warehouse Holds", icon: Package },
  { to: "/inventory/rmas", label: "Returns (RMA)", icon: ArrowUUpLeft },
  { to: "/inventory/purchase-orders", label: "Purchase Orders", icon: Truck },
];

const REPORT_ACTIONS = [
  { to: "/inventory/reporting", icon: ChartBar, label: "All Reports", testid: "nav-reporting" },
  { to: "/inventory/reporting?report=stock", icon: Package, label: "Stock on Hand", testid: "nav-reporting-stock" },
  { to: "/inventory/reporting?report=stock-detail", icon: Stack, label: "Stock Detail", testid: "nav-reporting-stock-detail" },
  { to: "/inventory/reporting?report=sales", icon: Receipt, label: "Sales", testid: "nav-reporting-sales" },
  { to: "/inventory/reporting?report=sales-forecast", icon: ChartBar, label: "Sales Forecast", testid: "nav-reporting-sales-forecast" },
  { to: "/inventory/reporting?report=margin", icon: Calculator, label: "Margin", testid: "nav-reporting-margin" },
  { to: "/inventory/reporting?report=benchmark", icon: ChartBar, label: "Business Benchmark", testid: "nav-reporting-benchmark" },
];

const CONTACT_CHILDREN = [
  { to: "/customers?view=organizations", match: "view=organizations", label: "Organizations", icon: Buildings, count: "organizations" },
  { to: "/customers?view=people", match: "view=people", label: "People", icon: UserCircle, count: "people" },
  { to: "/leads", match: "/leads", label: "Leads", icon: UsersThree, count: "leads" },
  { to: "/inventory/suppliers", match: "/inventory/suppliers", label: "Suppliers", icon: Package, perm: "read:purchasing" },
];

const SectionHeader = ({ icon: Icon, label, open, onToggle, testid, className = "" }) => (
  <button
    type="button"
    data-testid={testid}
    aria-expanded={open}
    onClick={onToggle}
    className={`nav-section-label flex w-full items-center gap-2 rounded-md px-2.5 pb-1 pt-1 text-left text-[10px] uppercase tracking-[0.2em] text-zinc-600 transition-colors hover:text-zinc-300 ${className}`}
  >
    <Icon size={11} weight="bold" /> {label}
    <span className="ml-2 h-px flex-1 bg-zinc-900" />
    <CaretDown
      size={10}
      weight="bold"
      className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
    />
  </button>
);

const NavLink = ({
  to,
  icon: Icon,
  label,
  active,
  testid,
  onClick,
  onMouseEnter,
  onMouseLeave,
  nested,
  badge = 0,
  count,
  className = "",
}) => (
  <Link
    to={to}
    data-testid={testid}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    className={`group relative flex items-center gap-3 rounded-xl py-2 text-[11px] font-semibold uppercase tracking-widest transition-all duration-150 ${
      nested ? "pl-5 pr-3" : "px-2.5"
    } ${className} ${
      active
        ? "bg-white/[0.04] text-white"
        : "text-zinc-500 hover:bg-zinc-800/60 hover:text-white"
    }`}
  >
    <span
      className={`flex shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ${
        nested ? "h-6 w-6" : "h-8 w-8"
      } ${
        active
          ? "crm-nav-active-icon bg-indigo-500/25 text-indigo-300"
          : "bg-zinc-800/60 text-zinc-500 group-hover:text-zinc-200"
      }`}
    >
      <Icon size={nested ? 14 : 16} weight="bold" />
    </span>
    <span className="truncate nav-label">{label}</span>
    {count !== undefined && badge === 0 && (
      <span
        data-testid={`${testid}-count`}
        className="nav-badge ml-auto rounded-md bg-zinc-800/80 px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none tracking-normal text-zinc-400"
      >
        {count}
      </span>
    )}
    {badge > 0 && (
      <span
        data-testid={`${testid}-badge`}
        className="nav-badge ml-auto rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-black leading-none text-white"
      >
        {badge > 99 ? "99+" : badge}
      </span>
    )}
  </Link>
);

// Collapsed-rail representation of a nav group: a single icon that reveals a
// hover flyout listing the group's children.
const GroupRailIcon = ({ icon: Icon, active, testid, onMouseEnter, onMouseLeave, className = "" }) => (
  <button
    type="button"
    data-testid={testid}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    className={`group relative flex items-center rounded-xl py-2 px-2.5 transition-all duration-150 ${className} ${
      active ? "bg-white/[0.04]" : "hover:bg-zinc-800/60"
    }`}
  >
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ${
        active
          ? "crm-nav-active-icon bg-indigo-500/25 text-indigo-300"
          : "bg-zinc-800/60 text-zinc-500 group-hover:text-zinc-200"
      }`}
    >
      <Icon size={16} weight="bold" />
    </span>
  </button>
);

export const photoSrc = (p) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.storage_path}?auth=${localStorage.getItem(
    "sw_token",
  )}`;

export const AppHeader = ({ title }) => {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const [logo, setLogo] = useState(null);
  const [counts, setCounts] = useState({});
  const [folded, setFolded] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sw_nav_folded") || "{}");
    } catch {
      return {};
    }
  });
  const toggleFold = (key) =>
    setFolded((f) => {
      const next = { ...f, [key]: !f[key] };
      localStorage.setItem("sw_nav_folded", JSON.stringify(next));
      return next;
    });
  const canAccounts = can("read:accounts");
  useEffect(() => {
    if (!canAccounts) return;
    api.get("/contacts/counts").then(({ data }) => setCounts(data || {})).catch(() => {});
  }, [pathname, canAccounts]);
  const [theme, setTheme] = useState(getTheme());
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sw_sidebar_collapsed") === "1",
  );
  const attention = useAttention();
  const blockers = can("read:quotes") ? blockerCount(attention.projects, attention.conflicts) : 0;

  useEffect(() => {
    document.documentElement.classList.toggle("sidebar-collapsed", collapsed);
    localStorage.setItem("sw_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    applyTheme(next);
  };

  useEffect(() => {
    api
      .get("/settings/branding")
      .then(({ data }) => {
        setLogo(data.header_logo || data.logo);
        if (data.favicon) {
          const el = document.getElementById("app-favicon");
          if (el) el.href = `${process.env.REACT_APP_BACKEND_URL}/api/public/favicon?t=${Date.now()}`;
        }
        if (applyShopTheme(data.shop_theme)) window.location.reload();
      })
      .catch(() => setLogo(null));
  }, []);

  const close = () => setOpen(false);

  // ---- Collapsed-rail hover flyout ------------------------------------------
  const [flyout, setFlyout] = useState(null); // { key, top, title, items, single }
  const flyTimer = useRef();
  const openFly = (key, title, items, single, el) => {
    if (!collapsed || !el) return;
    clearTimeout(flyTimer.current);
    const r = el.getBoundingClientRect();
    const est = 48 + Math.max(items?.length || 0, 1) * 40;
    setFlyout({
      key,
      top: Math.max(8, Math.min(r.top, window.innerHeight - est - 12)),
      title,
      items: items || [],
      single,
    });
  };
  const keepFly = () => clearTimeout(flyTimer.current);
  const closeFly = () => {
    clearTimeout(flyTimer.current);
    flyTimer.current = setTimeout(() => setFlyout(null), 130);
  };
  useEffect(() => {
    setFlyout(null);
  }, [pathname, search, collapsed]);

  // ---- Nav model ------------------------------------------------------------
  const navModel = [];
  if (can("read:quotes"))
    navModel.push({
      type: "link",
      to: "/needs-a-push",
      icon: Lightning,
      label: "Needs a push",
      testid: "nav-needs-push",
      badge: blockers,
      active: pathname.startsWith("/needs-a-push"),
    });
  navModel.push({
    type: "group",
    key: "dashboard",
    icon: SquaresFour,
    label: "Dashboard",
    testid: "nav-section-dashboard",
    items: [
      { to: "/", icon: SquaresFour, label: "Overview", testid: "nav-dashboard", active: pathname === "/" },
      ...(can("read:jobs")
        ? [{ to: "/projects/board", icon: Kanban, label: "Project board", testid: "nav-project-board", active: pathname.startsWith("/projects/board") }]
        : []),
      ...(can("read:quotes")
        ? [{ to: "/pipeline", icon: FunnelSimple, label: "Pipeline", testid: "nav-pipeline", active: pathname.startsWith("/pipeline") }]
        : []),
      { to: "/assistant", icon: Sparkle, label: "Assistant", testid: "nav-assistant", active: pathname === "/assistant" },
    ],
  });
  if (can("read:accounts"))
    navModel.push({
      type: "group",
      key: "accounts",
      icon: AddressBook,
      label: "Accounts",
      testid: "nav-section-accounts",
      mt: "mt-3",
      items: [
        {
          to: "/customers",
          icon: AddressBook,
          label: "All accounts",
          testid: "nav-customers",
          active: pathname.startsWith("/customers") && !search.includes("view="),
        },
        ...CONTACT_CHILDREN.filter((c) => !c.perm || can(c.perm)).map((c) => ({
          to: c.to,
          icon: c.icon,
          label: c.label,
          testid: `nav-contacts-${c.label.toLowerCase()}`,
          count: c.count ? counts[c.count] : undefined,
          active: c.match.startsWith("/")
            ? pathname.startsWith(c.match)
            : pathname.startsWith("/customers") && search.includes(c.match),
        })),
      ],
    });
  if (can("read:accounts"))
    navModel.push({
      type: "group",
      key: "inbox",
      icon: Tray,
      label: "Inbox",
      testid: "nav-section-inbox",
      mt: "mt-3",
      items: [
        { to: "/inbox/emails", icon: EnvelopeSimple, label: "Customer Communications", testid: "nav-inbox-emails", active: pathname === "/inbox" || pathname === "/inbox/emails" },
        { to: "/inbox/comments", icon: ChatCircle, label: "Portal Comments", testid: "nav-inbox-comments", active: pathname === "/inbox/comments" },
        { to: "/inbox/texts", icon: DeviceMobile, label: "Customer Texting", testid: "nav-inbox-texts", active: pathname === "/inbox/texts" },
      ],
    });
  if (can("read:jobs"))
    navModel.push({
      type: "link",
      to: "/projects",
      icon: Briefcase,
      label: "Projects",
      testid: "nav-jobs",
      mt: "mt-3",
      actions: [
        { to: "/projects", icon: Briefcase, label: "All Projects", testid: "nav-jobs" },
        { to: "/projects/new", icon: Plus, label: "New Project", testid: "nav-jobs-new" },
        { to: "/projects/board", icon: Kanban, label: "Project Board", testid: "nav-jobs-board" },
        { to: "/projects/schedule", icon: CalendarBlank, label: "Schedule", testid: "nav-jobs-schedule" },
      ],
      active:
        (pathname.startsWith("/projects") && !pathname.startsWith("/projects/board")) ||
        pathname.startsWith("/jobs"),
    });
  if (can("read:quotes"))
    navModel.push({
      type: "link",
      to: "/quotes",
      icon: Calculator,
      label: "Quotes",
      testid: "nav-quotes",
      active: pathname.startsWith("/quotes"),
      actions: [
        { to: "/quotes", icon: Calculator, label: "All Quotes", testid: "nav-quotes" },
        { to: "/quotes/new", icon: Plus, label: "New Quote", testid: "nav-quotes-new" },
      ],
    });
  if (can("read:quotes"))
    navModel.push({ type: "link", to: "/design", icon: PencilRuler, label: "Design Studio", testid: "nav-design", active: pathname.startsWith("/design") });
  if (can("read:quotes"))
    navModel.push({
      type: "link",
      to: "/invoices",
      icon: Receipt,
      label: "Invoices",
      testid: "nav-invoices",
      active: pathname.startsWith("/invoices"),
      actions: [
        { to: "/invoices", icon: Receipt, label: "All Invoices", testid: "nav-invoices" },
        { to: "/invoices?new=1", icon: Plus, label: "New Invoice", testid: "nav-invoices-new" },
      ],
    });
  if (can("read:quotes"))
    navModel.push({
      type: "link",
      to: "/services",
      icon: Wrench,
      label: "Services",
      testid: "nav-services",
      active: pathname.startsWith("/services"),
      actions: [
        { to: "/services", icon: Wrench, label: "All Services", testid: "nav-services" },
        { to: "/projects/service/new", icon: Plus, label: "New Service", testid: "nav-services-new" },
      ],
    });
  if (can("read:jobs"))
    navModel.push({
      type: "group",
      key: "planning",
      icon: NavigationArrow,
      label: "Planning",
      testid: "nav-section-planning",
      mt: "mt-3",
      items: [
        { to: "/route-planner", icon: NavigationArrow, label: "Route Planner", testid: "nav-route-planner", active: pathname === "/route-planner" },
        { to: "/calendar", icon: CalendarBlank, label: "Calendar", testid: "nav-calendar", active: pathname === "/calendar" },
      ],
    });
  if (can("read:inventory"))
    navModel.push({
      type: "group",
      key: "inventory",
      icon: Rows,
      label: "Inventory",
      testid: "nav-section-inventory",
      mt: "mt-4",
      items: [
        ...INVENTORY_CHILDREN.filter(
          (c) => !c.to.includes("purchase-orders") || can("read:purchasing"),
        ).map((c) => ({
          to: c.to,
          icon: c.icon,
          label: c.label,
          testid: `nav-inventory-${c.label.toLowerCase().replace(/ /g, "-")}`,
          active: pathname.startsWith(c.to),
        })),
        { to: "/inventory/slabs/new", icon: Plus, label: "Add Slab", testid: "nav-inventory-add-slab", active: pathname === "/inventory/slabs/new" },
      ],
    });
  if (can("read:inventory"))
    navModel.push({
      type: "link",
      to: "/inventory/reporting",
      icon: ChartBar,
      label: "Reporting",
      testid: "nav-reporting",
      mt: "mt-3",
      active: pathname.startsWith("/inventory/reporting"),
      actions: REPORT_ACTIONS,
    });
  if (can("read:showroom")) {
    navModel.push({ type: "link", to: "/showroom", icon: Storefront, label: "Showroom", testid: "nav-showroom", mt: "mt-4", active: pathname === "/showroom" });
    navModel.push({ type: "link", to: "/completion-photos", icon: Images, label: "Completion Photos", testid: "nav-completions", active: pathname === "/completion-photos" });
  }

  const menu = (pfx = "") => {
    const isCollapsedView = collapsed && pfx !== "m-";
    return (
      <nav className="crm-nav flex h-full flex-col gap-0.5 overflow-y-auto p-3">
        {navModel.map((entry) => {
          if (entry.type === "link") {
            return (
              <NavLink
                key={entry.testid}
                to={entry.to}
                icon={entry.icon}
                label={entry.label}
                active={entry.active}
                badge={entry.badge || 0}
                className={entry.mt || ""}
                testid={`${pfx}${entry.testid}`}
                onClick={close}
                onMouseEnter={(e) =>
                  openFly(
                    entry.testid,
                    entry.label,
                    entry.actions || [
                      { to: entry.to, icon: entry.icon, label: entry.label, testid: entry.testid },
                    ],
                    false,
                    e.currentTarget,
                  )
                }
                onMouseLeave={closeFly}
              />
            );
          }
          if (isCollapsedView) {
            return (
              <GroupRailIcon
                key={entry.key}
                icon={entry.icon}
                active={entry.items.some((i) => i.active)}
                className={entry.mt || ""}
                testid={`${pfx}${entry.testid}-rail`}
                onMouseEnter={(e) => openFly(entry.key, entry.label, entry.items, false, e.currentTarget)}
                onMouseLeave={closeFly}
              />
            );
          }
          return (
            <Fragment key={entry.key}>
              <SectionHeader
                icon={entry.icon}
                label={entry.label}
                open={!folded[entry.key]}
                onToggle={() => toggleFold(entry.key)}
                testid={`${pfx}${entry.testid}`}
                className={entry.mt || ""}
              />
              {!folded[entry.key] &&
                entry.items.map((it) => (
                  <NavLink
                    key={it.testid}
                    to={it.to}
                    icon={it.icon}
                    label={it.label}
                    nested
                    active={it.active}
                    count={it.count}
                    badge={it.badge || 0}
                    testid={`${pfx}${it.testid}`}
                    onClick={close}
                  />
                ))}
            </Fragment>
          );
        })}
        <div
          className={`mt-auto flex-col gap-0.5 border-t border-zinc-800 pt-3 ${pfx ? "flex" : "flex lg:hidden"}`}
        >
          <NavLink
            to="/account"
            icon={UserCircle}
            label="My Account"
            active={pathname === "/account"}
            testid={`${pfx}nav-account`}
            onClick={close}
          />
          <button
            data-testid={`${pfx}logout-button`}
            onClick={async () => {
              close();
              await logout();
              navigate("/login");
            }}
            className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-[11px] font-semibold uppercase tracking-widest text-zinc-500 transition-colors duration-150 hover:bg-zinc-900 hover:text-red-400"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-zinc-900/70">
              <SignOut size={16} weight="bold" />
            </span>
            <span className="nav-label">Sign Out</span>
          </button>
          <div className="sidebar-usercard mt-2 flex shrink-0 items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-2">
            {user?.icon ? (
              <img
                src={photoSrc(user.icon)}
                alt="You"
                data-testid="sidebar-user-icon"
                className="h-8 w-8 shrink-0 rounded-full border border-zinc-800 object-cover"
              />
            ) : (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-[11px] font-bold uppercase text-indigo-300">
                {(user?.name || user?.email || "?").slice(0, 2)}
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[11px] font-semibold text-zinc-200">
                {user?.name || "Crew"}
              </span>
              <span
                className="block truncate text-[10px] text-zinc-500"
                data-testid={`${pfx}sidebar-email`}
              >
                {user?.email}
              </span>
            </span>
          </div>
        </div>
      </nav>
    );
  };

  const brand = (
    <Link
      to="/"
      className="flex min-w-0 items-center gap-2.5"
      data-testid="brand-link"
      onClick={close}
    >
      {logo ? (
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}/api/public/header-logo`}
          alt="Brioni Granite"
          data-testid="brand-logo"
          className="h-9 w-auto max-w-[160px] rounded-lg object-contain"
        />
      ) : (
        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-indigo-400/30 bg-indigo-500/15 text-indigo-300">
          <Cube size={20} weight="bold" />
        </span>
      )}
    </Link>
  );

  const themeIcon = (
    <button
      data-testid="theme-toggle"
      onClick={toggleTheme}
      title={theme === "light" ? "Switch to dark" : "Switch to light"}
      aria-label="Toggle theme"
      className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-800 text-zinc-500 transition-colors duration-150 hover:border-indigo-400/40 hover:text-indigo-300 md:flex"
    >
      {theme === "light" ? (
        <Moon size={17} weight="bold" />
      ) : (
        <Sun size={17} weight="bold" />
      )}
    </button>
  );

  const mobileIcon = (
    <button
      data-testid="switch-to-mobile-button"
      onClick={() => {
        localStorage.setItem("sw_mobile_mode", "1");
        navigate("/mobile");
      }}
      title="Field crew mobile app"
      aria-label="Switch to field crew mobile app"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-800 text-zinc-500 transition-colors duration-150 hover:border-indigo-400/40 hover:text-indigo-300"
    >
      <DeviceMobile size={17} weight="bold" />
    </button>
  );

  const settingsIcon = user?.role === "admin" && (
    <Link
      to="/settings"
      data-testid="settings-icon-link"
      title="Settings"
      aria-label="Settings"
      onClick={close}
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 ${
        pathname.startsWith("/settings") || pathname === "/crew"
          ? "border-indigo-400/40 bg-indigo-500/15 text-indigo-300"
          : "border-zinc-800 text-zinc-500 hover:border-indigo-400/40 hover:text-indigo-300"
      }`}
    >
      <Gear size={17} weight="bold" />
    </Link>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        data-testid="app-sidebar"
        className="no-print fixed left-0 top-0 z-[70] hidden h-screen w-60 flex-col border-r border-zinc-800 bg-zinc-950 transition-[width] duration-200 lg:flex"
      >
        <div className="sidebar-header flex h-16 shrink-0 items-center gap-2 border-b border-zinc-900 px-4">
          <span className="brand-wrap flex min-w-0 flex-1 items-center">
            {brand}
          </span>
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            aria-label="Toggle menu width"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-zinc-500 transition-colors duration-150 hover:bg-zinc-800/60 hover:text-white"
          >
            <SidebarSimple size={18} weight="bold" />
          </button>
        </div>
        <div className="min-h-0 flex-1">{menu()}</div>
      </aside>

      {/* Collapsed-rail hover flyout (desktop only) */}
      {collapsed && flyout && (
        <div
          data-testid="nav-flyout"
          onMouseEnter={keepFly}
          onMouseLeave={closeFly}
          className="crm-nav-flyout no-print fixed z-[80] hidden min-w-[210px] rounded-2xl border border-zinc-800 bg-zinc-950/95 p-2 shadow-2xl backdrop-blur-md lg:block"
          style={{ top: flyout.top, left: "4.5rem" }}
        >
          <div className="mb-1 border-b border-zinc-800 px-2 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            {flyout.title}
          </div>
          {flyout.items.map((it) => {
            const Ico = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`flyout-${it.testid}`}
                onClick={() => {
                  close();
                  setFlyout(null);
                }}
                className={`flex items-center gap-3 rounded-lg px-2 py-2 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  it.active ? "bg-white/[0.04] text-white" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-white"
                }`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                    it.active ? "bg-indigo-500/25 text-indigo-300" : "bg-zinc-800/60 text-zinc-500"
                  }`}
                >
                  <Ico size={14} weight="bold" />
                </span>
                <span className="truncate">{it.label}</span>
                {it.count !== undefined && (
                  <span className="ml-auto rounded-md bg-zinc-800/80 px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none text-zinc-400">
                    {it.count}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {/* Mobile top bar */}
      <header className="no-print sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-zinc-800 bg-zinc-950 px-4 lg:hidden">
        <button
          data-testid="menu-toggle"
          onClick={() => setOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-zinc-300 transition-colors duration-150 hover:bg-zinc-900 hover:text-yellow-400"
          aria-label="Open menu"
        >
          <List size={22} weight="bold" />
        </button>
        {brand}
        <span className="ml-auto flex items-center gap-2">
          <GlobalSearchMobile />
          <AlertsBell />
          {mobileIcon}
          {themeIcon}
          {settingsIcon}
          <TasksPopover />
        </span>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div
          className="no-print fixed inset-0 z-50 lg:hidden"
          data-testid="mobile-menu"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={close}
            data-testid="menu-overlay"
          />
          <div className="absolute left-0 top-0 flex h-full w-72 max-w-[85%] flex-col border-r border-zinc-800 bg-zinc-950 shadow-2xl">
            <div className="flex h-16 shrink-0 items-center gap-2 border-b border-zinc-900 px-4">
              {brand}
              <button
                data-testid="menu-close"
                onClick={close}
                className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-900 hover:text-white"
                aria-label="Close menu"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="min-h-0 flex-1">{menu("m-")}</div>
          </div>
        </div>
      )}

      {/* Desktop page title strip */}
      <div className="app-shell-offset no-print relative z-[60] hidden h-16 items-center gap-3 border-b border-zinc-900 bg-zinc-950/80 text-[11px] uppercase tracking-[0.2em] text-zinc-600 backdrop-blur-md lg:flex">
        <span className="h-1.5 w-1.5 rounded-full bg-indigo-400/70" />
        {title}
        <div className="mx-auto w-full max-w-sm normal-case tracking-normal">
          <GlobalSearch />
        </div>
        <span className="ml-auto flex items-center gap-2 normal-case tracking-normal">
          <AlertsBell />
          <TasksPopover />
          {mobileIcon}
          {themeIcon}
          {settingsIcon}
          <Link
            to="/account"
            data-testid="account-icon-link"
            title="My account"
            aria-label="My account"
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 ${
              pathname === "/account"
                ? "border-indigo-400/40 bg-indigo-500/15 text-indigo-300"
                : "border-zinc-800 text-zinc-500 hover:border-indigo-400/40 hover:text-indigo-300"
            }`}
          >
            <UserCircle size={17} weight="bold" />
          </Link>
          <button
            data-testid="desktop-logout-button"
            title="Sign out"
            aria-label="Sign out"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-800 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
          >
            <SignOut size={17} weight="bold" />
          </button>
          <span
            className="flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900 py-1 pl-1 pr-3"
            data-testid="desktop-profile"
          >
            {user?.icon ? (
              <img
                src={photoSrc(user.icon)}
                alt="You"
                data-testid="desktop-user-icon"
                className="h-7 w-7 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-[10px] font-bold uppercase text-indigo-300">
                {(user?.name || user?.email || "?").slice(0, 2)}
              </span>
            )}
            <span className="max-w-[160px] truncate text-xs font-semibold text-zinc-200">
              {user?.name || user?.email}
            </span>
          </span>
        </span>
      </div>
    </>
  );
};
